import React, {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
  Body,
  Headline,
  Icon,
  Label,
  PoppaysLoader,
  Tabs,
  designSystemToken,
} from '@lightricks/react-design-system';
import countryFullName from '@/utils/getCountryFullName';
import getPlatformDisplayNameFromProvider from '@/utils/getPlatformDisplayNameFromProvider';
import {raiseFlashMessageError} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import type {
  OffPlatformCreatorSnapshotIdentity,
  OffPlatformPost,
} from '@/api/fetchers/crm/offPlatformCreators';
import profilePageStyles from '@/views/creators/profile/Profile.module.scss';
import CreatorProfileAvatar from '@/components/creator-profile-avatar';
import profileStyles from '@/components/creator-profile/CreatorProfile.module.scss';
import overviewStyles from '@/components/creator-profile/components/creator-profile-tab-content/tabs/overview-tab/OverviewTab.module.scss';
import tabStyles from '@/components/creator-profile/components/creator-profile-tabs/CreatorProfileTabs.module.scss';
import headerStyles from '@/components/creator-profile/components/header/Header.module.scss';
import metricsStyles from '@/components/creator-profile/components/metrics-summary-card/MetricsSummaryCard.module.scss';
import selectorStyles from '@/components/creator-profile/components/social-network-selector/SocialNetworkSelector.module.scss';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import DropdownButton from '@/components/dropdown-button';
import {Option} from '@/components/dropdown-button/DropdownButton';
import OffPlatformBadge from '@/components/off-platform-badge/OffPlatformBadge';
import Page from '@/components/page';
import SocialNetworkIcon from '@/components/social-network-icon';
import StatCard from '@/components/stat-card';
import ToggleableTags from '@/components/toggleable-tags';
import useOffPlatformCreatorQuery from '@/hooks/queries/use-off-platform-creator-query';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import useParams from '@/hooks/use-params';
import OffPlatformAudience from './OffPlatformAudience';
import OffPlatformInviteAction from './OffPlatformInviteAction';
import OffPlatformPlatformFollowers from './OffPlatformPlatformFollowers';
import OffPlatformPostGrid from './OffPlatformPostGrid';
import styles from './OffPlatformProfile.module.scss';

function uniquePosts(posts: OffPlatformPost[]) {
  const seen = new Set<string>();
  return posts.filter((post) => {
    if (seen.has(post.url)) {
      return false;
    }
    seen.add(post.url);
    return true;
  });
}

function countryLabel(country?: string | null) {
  if (!country) {
    return '';
  }
  return countryFullName(country.toUpperCase()) || country;
}

function hasPaidReport(identity: OffPlatformCreatorSnapshotIdentity) {
  return identity.audience != null || identity.popularPosts != null;
}

function sortByFollowers(identities: OffPlatformCreatorSnapshotIdentity[]) {
  return [...identities].sort(
    (left, right) => (right.followerCount ?? 0) - (left.followerCount ?? 0)
  );
}

function usePaidOffPlatformNetwork(
  identities: OffPlatformCreatorSnapshotIdentity[]
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const paidIdentities = useMemo(
    () => sortByFollowers(identities.filter(hasPaidReport)),
    [identities]
  );
  const requested = searchParams.get('activeSocialNetwork');
  const activeIdentity = useMemo(() => {
    return (
      paidIdentities.find((identity) => identity.provider === requested) ||
      paidIdentities[0]
    );
  }, [paidIdentities, requested]);

  const onSelect = (identity: OffPlatformCreatorSnapshotIdentity) => {
    const next = new URLSearchParams(searchParams);
    next.set('activeSocialNetwork', identity.provider);
    setSearchParams(next);
  };

  return {paidIdentities, activeIdentity, onSelect};
}

function OffPlatformTabNetworkSelector({
  identities,
  activeProvider,
  onSelect,
}: {
  identities: OffPlatformCreatorSnapshotIdentity[];
  activeProvider?: string;
  onSelect: (identity: OffPlatformCreatorSnapshotIdentity) => void;
}) {
  if (identities.length === 0) {
    return null;
  }

  return (
    <div className={selectorStyles.socialNetworksSelectorContainer}>
      {identities.map((identity) => (
        <div
          key={identity.provider}
          className={`${selectorStyles.socialNetworkItem} ${
            identity.provider === activeProvider ? selectorStyles.active : ''
          }`}
          onClick={() => onSelect(identity)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onSelect(identity);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Body size="lg">
            {getPlatformDisplayNameFromProvider(identity.provider)}
          </Body>
        </div>
      ))}
    </div>
  );
}

function IdentityHandle({
  identity,
}: {
  identity: OffPlatformCreatorSnapshotIdentity;
}) {
  const handle = <Body size="md">@{identity.handle}</Body>;
  if (!identity.profileUrl) {
    return handle;
  }

  return (
    <a
      className={metricsStyles.handleLink}
      href={identity.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {handle}
    </a>
  );
}

function OffPlatformProfile({customLoader}: {customLoader?: React.ReactNode}) {
  const {offPlatformCreatorId} = useParams();
  const brandId = useBrandId();
  const navigation = useNavigation();
  const [activeTab, setActiveTab] = useState('overview');
  const tabsData = [
    {value: 'overview', label: translate('Overview')},
    {value: 'audience', label: translate('Audience')},
    {value: 'posts', label: translate('Posts')},
  ];
  const {data, error, isLoading, isError} = useOffPlatformCreatorQuery({
    id: offPlatformCreatorId,
    brandId,
  });
  const {paidIdentities, activeIdentity, onSelect} = usePaidOffPlatformNetwork(
    data?.identities ?? []
  );

  useEffect(() => {
    if (!error) {
      return;
    }
    const status = Number(
      (error as {response?: {status?: number}}).response?.status
    );
    if (status === 404) {
      navigation.navigate(ROUTES.NOT_FOUND);
      return;
    }
    raiseFlashMessageError();
  }, [error, navigation]);

  const location = useMemo(() => {
    if (!data) {
      return '';
    }
    return [data.city, data.region, countryLabel(data.country)]
      .filter(Boolean)
      .join(', ');
  }, [data]);

  const infoLine = useMemo(() => {
    if (!data) {
      return '';
    }
    return [data.ageGroup, data.gender, location].filter(Boolean).join(' | ');
  }, [data, location]);

  const fallbackIdentity = data
    ? sortByFollowers(data.identities)[0]
    : undefined;
  const metricsIdentity = activeIdentity ?? fallbackIdentity;
  const popularPosts = activeIdentity?.popularPosts ?? data?.popularPosts ?? [];
  const recentPosts = activeIdentity?.recentPosts ?? data?.recentPosts ?? [];
  const allPosts = useMemo(
    () => uniquePosts([...popularPosts, ...recentPosts]),
    [popularPosts, recentPosts]
  );
  const overviewPosts = popularPosts
    .filter((post) => post.thumbnail)
    .slice(0, 6);
  const audience = activeIdentity?.audience ?? data?.audience;
  const dropdownOptions: Option[] = paidIdentities.map((identity) => ({
    id: identity.provider,
    label: getPlatformDisplayNameFromProvider(identity.provider),
  }));

  if (isLoading || (!data && !isError)) {
    return customLoader || <PoppaysLoader />;
  }

  if (isError || !data) {
    return null;
  }

  const handlePlatformChange = (option: Option) => {
    const identity = paidIdentities.find((row) => row.provider === option.id);
    if (identity) {
      onSelect(identity);
    }
  };

  return (
    <Page
      id="off-platform-profile"
      testID="off-platform-profile"
      className={profilePageStyles.profileContainer}
      childrenClassName={profilePageStyles.profileWrapper}
    >
      <div className={profileStyles.creatorProfileContainer}>
        <div className={headerStyles.headerContainer}>
          <div className={headerStyles.avatarSection}>
            <CreatorProfileAvatar
              className={headerStyles.headerAvatar}
              profileImageUrl={data.avatarUrl || ''}
              profileDisplayName={data.displayName || data.email}
              size="3xl"
              allowEnlarge
            />
          </div>
          <div className={headerStyles.contentSection}>
            <div className={headerStyles.topRow}>
              <div className={headerStyles.nameColumn}>
                <div className={headerStyles.nameAndInfo}>
                  <div className={styles.nameRow}>
                    <Headline
                      data-testid={
                        CREATOR_PROFILE_TEST_IDS.HEADER__DISPLAY_NAME
                      }
                      size="lg"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      {data.displayName || data.email}
                    </Headline>
                    <OffPlatformBadge />
                  </div>
                  {infoLine ? (
                    <Body
                      data-testid={
                        CREATOR_PROFILE_TEST_IDS.HEADER__PROFILE_INFO
                      }
                      size="lg"
                    >
                      {infoLine}
                    </Body>
                  ) : null}
                  <Body size="sm">
                    <a
                      className={styles.emailLink}
                      href={`mailto:${data.email}`}
                    >
                      {data.email}
                    </a>
                  </Body>
                  <OffPlatformInviteAction creator={data} />
                </div>
              </div>
              <OffPlatformPlatformFollowers identities={data.identities} />
            </div>
          </div>
        </div>
        <div className={tabStyles.tabsContainer}>
          <Tabs
            ScrollButtonComponent="div"
            size="large"
            appearance="brand"
            tabsData={tabsData}
            value={activeTab}
            onChange={(_, tabId) => setActiveTab(String(tabId))}
          />
        </div>
        <div className={profileStyles.tabContentContainer}>
          {activeTab === 'overview' ? (
            <div className={styles.overviewColumn}>
              <div className={overviewStyles.aboutMeSection}>
                <Headline size="sm">{translate('About')}</Headline>
                {data.bio ? (
                  <Body
                    size="md"
                    data-testid={CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__BIO}
                  >
                    {data.bio}
                  </Body>
                ) : null}
                <ToggleableTags
                  tags={(data.interests ?? []).map((interest) => interest.name)}
                  size="md"
                />
              </div>
              <div className={metricsStyles.container}>
                {metricsIdentity ? (
                  <div className={metricsStyles.topBar}>
                    {paidIdentities.length > 0 && activeIdentity ? (
                      <DropdownButton
                        testID={
                          CREATOR_PROFILE_TEST_IDS.TAB__OVERVIEW__METRICS_SUMMARY__PLATFORM_DROPDOWN
                        }
                        buttonClassName={metricsStyles.platformDropdown}
                        options={dropdownOptions}
                        selectedOptionIds={[activeIdentity.provider]}
                        label={getPlatformDisplayNameFromProvider(
                          activeIdentity.provider
                        )}
                        onChange={handlePlatformChange}
                        size="large"
                        iconSize="small"
                        mode="plain"
                        appearance="neutral"
                        startIcon={
                          <SocialNetworkIcon
                            name={activeIdentity.provider}
                            width={20}
                            height={20}
                          />
                        }
                      />
                    ) : (
                      <div className={metricsStyles.handleInfo}>
                        <SocialNetworkIcon
                          name={metricsIdentity.provider}
                          width={20}
                          height={20}
                        />
                      </div>
                    )}
                    <IdentityHandle identity={metricsIdentity} />
                  </div>
                ) : null}
                <div className={metricsStyles.content}>
                  <Headline size="sm">{translate('Metrics')}</Headline>
                  <div className={metricsStyles.metricCardsRow}>
                    <StatCard
                      iconName="Navigation-Profile"
                      label="Followers"
                      value={metricsIdentity?.followerCount ?? undefined}
                    />
                    <StatCard
                      iconName="Features-Pitch-TurnOn"
                      label="Eng rate"
                      value={metricsIdentity?.engagementRate ?? undefined}
                      format="percentage"
                    />
                    <StatCard
                      iconName="Navigation-Heart-Line"
                      label="Avg likes"
                      value={
                        activeIdentity?.avgLikes ?? data.avgLikes ?? undefined
                      }
                    />
                    <StatCard
                      iconName="Navigation-Heart-Line"
                      label="Avg engagements"
                      value={
                        activeIdentity?.engagements ??
                        data.engagements ??
                        undefined
                      }
                    />
                    <StatCard
                      iconName="Features-Eye"
                      label="Avg views"
                      value={
                        activeIdentity?.avgViews ?? data.avgViews ?? undefined
                      }
                    />
                  </div>
                </div>
              </div>
              {overviewPosts.length > 0 ? (
                <div className={overviewStyles.portfolioSection}>
                  <Headline size="sm">{translate('Posts')}</Headline>
                  <OffPlatformPostGrid posts={overviewPosts} />
                  <button
                    className={overviewStyles.portfolioFooter}
                    type="button"
                    onClick={() => setActiveTab('posts')}
                  >
                    <Label size="md">{translate('See posts')}</Label>
                    <Icon
                      size="small"
                      appearance="neutral"
                      name="Actions-Arrow-Forward-Small"
                    />
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          {activeTab === 'audience' ? (
            <div className={styles.tabPane}>
              <OffPlatformTabNetworkSelector
                identities={paidIdentities}
                activeProvider={activeIdentity?.provider}
                onSelect={onSelect}
              />
              <OffPlatformAudience
                genders={audience?.genders}
                ages={audience?.ages}
                geoCountries={audience?.geoCountries}
                languages={audience?.languages}
              />
            </div>
          ) : null}
          {activeTab === 'posts' ? (
            <div className={styles.tabPane}>
              <OffPlatformTabNetworkSelector
                identities={paidIdentities}
                activeProvider={activeIdentity?.provider}
                onSelect={onSelect}
              />
              <OffPlatformPostGrid posts={allPosts} />
            </div>
          ) : null}
        </div>
      </div>
    </Page>
  );
}

export default OffPlatformProfile;
